import React, { useCallback, useContext, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ACCOUNT_TYPE_AGENT } from 'constants/index';
import { RESOURCE_LIBRARY_OPENED_TABLE_PATH, RESOURCE_LIBRARY_PATH } from 'constants/routes';
import { AGENT_PERMISSION_RESOURCE_LIBRARY, useIsSubscriptionAllowed } from 'hooks/useIsSubscriptionAllowed';
import useQueryParams from 'hooks/useQueryParams';
import { resetSearchResultCountersAction } from 'store/admin/properties';
import { dataSelector, loadingSelector, paginationSelector } from 'store/admin/properties/selectors';
import { searchResultCountersSelector } from 'store/admin/properties/selectors';
import { fetchThunk } from 'store/admin/properties/thunks';
import { getResourceLibrarySearchCountersThunk } from 'store/admin/properties/thunks';

import { useTableCollapsing, useUpdatingSearchResultCounters } from '../shared/functions';

export const ResourceLibraryContext = React.createContext(null);
export const useResourceLibraryContext = () => useContext(ResourceLibraryContext);

export const useResourceLibraryContextValue = ({ content }) => {
  const dispatch = useDispatch();
  const data = useSelector(dataSelector);
  const pagination = useSelector(paginationSelector);
  const isLoading = useSelector(loadingSelector);
  const { setQ, q, queryParams, setSort, setPage, setLimit } = useQueryParams({ defaultLimit: 25 });
  const isAllowed = useIsSubscriptionAllowed(ACCOUNT_TYPE_AGENT, AGENT_PERMISSION_RESOURCE_LIBRARY);
  const [previewModalData, setPreviewModalData] = useState({ isOpen: false });
  const searchResultCounters = useSelector(searchResultCountersSelector);
  useUpdatingSearchResultCounters(getResourceLibrarySearchCountersThunk, resetSearchResultCountersAction);

  const handleCollapse = useTableCollapsing({
    content,
    collapsedTablesRoute: RESOURCE_LIBRARY_PATH,
    expandTableRoute: RESOURCE_LIBRARY_OPENED_TABLE_PATH,
  });

  const handleOpenPreview = useCallback((pageId, params) => {
    setPreviewModalData({ isOpen: true, pageId, ...params });
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewModalData({ isOpen: false });
  }, []);

  useEffect(() => {
    const fetchPages = () =>
      dispatch(
        fetchThunk({
          content,
          orderBy: queryParams.orderBy,
          order: queryParams.order,
          limit: queryParams.limit,
          q: queryParams.q,
          page: queryParams.page,
        }),
      );

    if (!content) {
      return;
    }
    fetchPages();
  }, [dispatch, content, queryParams]);

  return {
    data,
    isLoading,
    pagination,
    setLimit,
    setPage,
    setSort,
    setQ,
    q,
    queryParams,
    isAllowed,
    previewModalData,
    handleOpenPreview,
    handleClosePreview,
    searchResultCounters,
    handleCollapse,
  };
};
